import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'common/css/master.css';
import QuestionPaperPdf from './questionPaperPdf';
import { PDFDownloadLink, PDFViewer, pdf } from '@react-pdf/renderer';
import QRCode from 'qrcode';
import {
    getClassListSvc,
    getSubjectListSvc,
    getExamListSvc,
    getQpPatternListSvc,
    getPatternSectionListSvc,
    generatePaperSvc,
    getTokenId,
} from './questionPaperGeneratorSvc';


// window.location.origin use karte hain (hardcoded prod domain nahi) — same reason as questionPaperQrCtrl.js
const QR_BASE_URL = window.location.origin + '/questionPaperGenerator';

function QuestionPaperGeneratorCtrl() {
    const [lstClass, setLstClass]     = useState([]);
    const [lstSubject, setLstSubject] = useState([]);
    const [lstExam, setLstExam]       = useState([]);
    const [lstPattern, setLstPattern] = useState([]);

    const [clsId, setClsId]           = useState(0);
    const [subjectId, setSubjectId]   = useState(0);
    const [examId, setExamId]         = useState(0);
    const [patternId, setPatternId]   = useState(0);
    const [lstSection, setLstSection] = useState([]);   // selected pattern ke sections (table ke liye)

    const [loading, setLoading]       = useState(false);
    const [result, setResult] = useState(null);
    const [showAnswers, setShowAnswers] = useState(false);

    const [qrDataUrl, setQrDataUrl] = useState('');

    // Agar URL mein ?tokenid=... hai (Question Paper QR page se aaye, jo alag menu/page hai), toh
    // localStorage mein save karo taaki getTokenId() (Svc.js) isse pick kare — sabse pehle chalna chahiye.
    useEffect(() => {
        const urlToken = new URLSearchParams(window.location.search).get('tokenid');
        if (urlToken) {
            localStorage.setItem('tokenid', urlToken);
        }
    }, []);

    // Classes (sirf qp_generation_allowed=1) aur Exams — mount pe ek baar load
    useEffect(() => {
        getClassListSvc((res) => {
            if (res.status === 'Success') {
                const classes = res.lstClass || [];
                setLstClass(classes);
                if (classes.length) setClsId(classes[0].clsId);   // pehli class auto-select
            }
        });
        getExamListSvc((res) => {
            if (res.status === 'Success') {
                const exams = res.lstExam || [];
                setLstExam(exams);
                if (exams.length) setExamId(exams[0].examId);   // pehla exam auto-select
            }
        });
    }, []);

    // subjects load on class change (backend ab clsId ke hisaab se resolve karta hai)
    useEffect(() => {
        setSubjectId(0); setLstSubject([]);
        if (!clsId) return;
        getSubjectListSvc(clsId, (res) => {
            if (res.status === 'Success') {
                const subs = res.lstSubject || [];
                setLstSubject(subs);
                if (subs.length) setSubjectId(subs[0].subId);   // pehla subject auto-select
            }
        });
    }, [clsId]);

    // patterns load on class change (from Java backend)
    useEffect(() => {
        setPatternId(0); setLstPattern([]);
        if (!clsId) return;
        getQpPatternListSvc(clsId, (res) => {
            if (res.status === 'Success') setLstPattern(res.lstPattern || []);   // user khud select kare
        });
    }, [clsId]);

    // sections load on pattern change — table dikhane ke liye
    useEffect(() => {
        setLstSection([]);
        if (!patternId) return;
        getPatternSectionListSvc(patternId, (res) => {
            if (res.status === 'Success') setLstSection(res.lstPatternSection || []);
        });
    }, [patternId]);


    useEffect(() => {
    // QR ke URL mein current session ka tokenid bhi bhejte hain, taaki mobile pe scan karne par
    // us fresh browser ki API calls bhi isi (real) session se authenticate ho — DEMO_TOKEN pe depend na rahe.
    const qrUrl = QR_BASE_URL + '?tokenid=' + encodeURIComponent(getTokenId());
    QRCode.toDataURL(qrUrl, { width: 200, margin: 1 })
        .then(url => {
            console.log('✅ QR generated');
            setQrDataUrl(url);
        })
        .catch(err => console.error('❌ QR error:', err));
}, []);

    const clsLabel     = () => (lstClass.find((c) => String(c.clsId) === String(clsId)) || {}).cls || '';
    const subjectLabel = () => (lstSubject.find((s) => String(s.subId) === String(subjectId)) || {}).sub || '';
    const examLabel     = () => (lstExam.find((e) => String(e.examId) === String(examId)) || {}).exam || '';
    const patternName  = () => (lstPattern.find((p) => String(p.patternId) === String(patternId)) || {}).pattern || '';

    const handleGenerate = () => {
        if (!clsId)      return toast.warn('Please select a class');
        if (!subjectId)  return toast.warn('Please select a subject');
        if (!examId)     return toast.warn('Please select an exam');
        if (!patternId)  return toast.warn('Please select a pattern');
        if (lstSection.length === 0) return toast.error('Selected pattern has no sections');

        setLoading(true); setResult(null);
        console.log('%c📤 GENERATE PAYLOAD', 'color:#6366f1;font-weight:bold;font-size:13px', { patternId, clsId, subjectId, examId });
        generatePaperSvc(patternId, clsId, subjectId, examId, async (gen) => {
            setLoading(false);
            if (gen.status !== 'Success') {
                return toast.error(gen.message || 'Generation failed');
            }
            console.log('%c📥 API JSON RESPONSE', 'color:#16a34a;font-weight:bold;font-size:13px', gen);
            setResult(gen);
            try {
                // PDF ready hone par USI tab mein kholo (blank-tab jump nahi)
                const blob = await pdf(
                    <QuestionPaperPdf result={gen} cls={clsLabel()} subject={subjectLabel()} patternName={patternName()} qrDataUrl={qrDataUrl} />
                ).toBlob();
                const url = URL.createObjectURL(blob);
                window.location.href = url;   // same tab → seedha PDF
            } catch (e) {
                console.error('PDF open error:', e);
                toast.error('PDF generate nahi ho payi');
            }
        });
    };

    // marks per section = marks/Q × questions to attempt (matches Pattern Creator's Total Marks calc)
    const sectionMarks = (sec) => (parseFloat(sec.qusMm) || 0) * (parseInt(sec.qusToAttempt) || 0);
    const totalMarks   = () => lstSection.reduce((sum, s) => sum + sectionMarks(s), 0);

    const difficultyClass = (d) => {
        const x = (d || '').toLowerCase();
        if (x.includes('eas')) return 'qpg-diff-easy';
        if (x.includes('med')) return 'qpg-diff-medium';
        return 'qpg-diff-hard';
    };

    return (
        <div className="MainContainer" style={{ pointerEvents: loading ? 'none' : 'auto', width: '100%', maxWidth: '100vw', overflowX: 'hidden', boxSizing: 'border-box' }}>
            {/* Loader */}
            <div style={{ position: 'fixed', zIndex: 10000, width: 100, height: 100,
                          marginLeft: -50, marginTop: -50, left: '50%', top: '50%',
                          display: loading ? 'block' : 'none' }}>
                <img className="loderImg" src={require('common/images/loader.gif')} alt="Loader..." />
            </div>

            {/* Manual page header (shared Header hataya — login-dependent tha) */}
            <div style={{ background: '#3200FF', color: '#fff', padding: '16px 18px', textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Generate Question Paper</h3>
            </div>
            <ToastContainer autoClose={3000} hideProgressBar />

            {/* QR ab alag page/menu ('Question Paper QR') pe hai — yahan seedha dropdowns dikhte hain */}
            <div style={{ width: '100%', maxWidth: 960, margin: '0 auto', padding: 15, boxSizing: 'border-box', textAlign: 'left' }}>

                {/* Selection — chaaro dropdown ek row mein (side by side) */}
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                    <div className="form-group" style={{ width: '100%', maxWidth: 420, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <label className="control-label" style={{ width: 70, margin: 0, flexShrink: 0 }}>Class</label>
                        <select className="form-control" style={{ flex: 1, minWidth: 0 }} value={clsId} onChange={(e) => setClsId(e.target.value)}>
                            {lstClass.map((c) => (
                                <option key={c.clsId} value={c.clsId}>{c.cls}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group" style={{ width: '100%', maxWidth: 420, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <label className="control-label" style={{ width: 70, margin: 0, flexShrink: 0 }}>Subject</label>
                        <select className="form-control" style={{ flex: 1, minWidth: 0 }} value={subjectId} onChange={(e) => setSubjectId(e.target.value)} disabled={!clsId}>
                            {lstSubject.map((s) => <option key={s.subId} value={s.subId}>{s.sub}</option>)}
                        </select>
                    </div>
                    <div className="form-group" style={{ width: '100%', maxWidth: 420, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <label className="control-label" style={{ width: 70, margin: 0, flexShrink: 0 }}>Exam</label>
                        <select className="form-control" style={{ flex: 1, minWidth: 0 }} value={examId} onChange={(e) => setExamId(e.target.value)}>
                            {lstExam.map((ex) => <option key={ex.examId} value={ex.examId}>{ex.exam}</option>)}
                        </select>
                    </div>
                    <div className="form-group" style={{ width: '100%', maxWidth: 420, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <label className="control-label" style={{ width: 70, margin: 0, flexShrink: 0 }}>Pattern</label>
                        <select className="form-control" style={{ flex: 1, minWidth: 0 }} value={patternId} onChange={(e) => setPatternId(e.target.value)} disabled={!clsId}>
                            <option value={0}>{clsId ? 'Select pattern' : 'Select class first'}</option>
                            {lstPattern.map((p) => <option key={p.patternId} value={p.patternId}>{p.pattern}</option>)}
                        </select>
                    </div>
                </div>

                {/* Pattern sections table (chapters auto-selected — UI pe nahi) */}
                {patternId > 0 && lstSection.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                        <h4 className="text-center" style={{ margin: '0 0 6px', fontWeight: 600 }}>Pattern Sections</h4>
                        <div style={{ textAlign: 'right', marginBottom: 6 }}>
                            <span style={{ fontSize: 14 }}><strong>Total Marks:</strong> {totalMarks()}</span>
                        </div>
                        <table className="tableLayout1" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <colgroup>
                                <col width="14%" />
                                <col width="34%" />
                                <col width="18%" />
                                <col width="16%" />
                                <col width="18%" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Section</th>
                                    <th style={{ textAlign: 'center' }}>Question Type</th>
                                    <th style={{ textAlign: 'center' }}>Questions</th>
                                    <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>Marks/Q</th>
                                    <th style={{ textAlign: 'center' }}>Marks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lstSection.map((sec, idx) => (
                                    <tr key={idx}>
                                        <td className="text-center" style={{ verticalAlign: 'middle' }}>{sec.section || String.fromCharCode(65 + idx)}</td>
                                        <td className="text-left" style={{ verticalAlign: 'middle' }}>{sec.qusType}</td>
                                        <td className="text-right" style={{ verticalAlign: 'middle' }}>{sec.noOfQus}</td>
                                        <td className="text-center" style={{ verticalAlign: 'middle' }}>{sec.qusMm}</td>
                                        <td className="text-right" style={{ verticalAlign: 'middle' }}>{sectionMarks(sec)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Actions — dono buttons ek hi line (baseline) pe */}
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={handleGenerate} disabled={loading}
                            style={{ flex: '1 1 220px', maxWidth: 300, minWidth: 0, whiteSpace: 'nowrap' }}>
                        {loading ? 'Generating…' : 'Generate Question Paper'}
                    </button>
                    {result && (
                        <PDFDownloadLink
                            document={
                                <QuestionPaperPdf
                                    result={result}
                                    cls={clsLabel()}
                                    subject={subjectLabel()}
                                    patternName={patternName()}
                                    qrDataUrl={qrDataUrl}
                                />
                            }
                            fileName="question-paper.pdf"
                            style={{ flex: '1 1 160px', maxWidth: 220, minWidth: 0 }}
                        >
                            {({ loading: pdfLoading }) => (
                                <button className="btn btn-default" disabled={pdfLoading} style={{ width: '100%' }}>
                                    {pdfLoading ? 'Preparing PDF...' : 'Download PDF'}
                                </button>
                            )}
                        </PDFDownloadLink>
                    )}
                </div>

                {/* DEV ONLY — live PDF preview (test ke baad hata dena) */}
                {result && (
                    <div style={{ marginTop: 20 }}>
                        <PDFViewer style={{ width: '100%', height: 600, border: '1px solid #ccc' }}>
                            <QuestionPaperPdf
                                result={result}
                                cls={clsLabel()}
                                subject={subjectLabel()}
                                patternName={patternName()}
                                qrDataUrl={qrDataUrl}
                            />
                        </PDFViewer>
                    </div>
                )}
            </div>
        </div>
    );
}

export default QuestionPaperGeneratorCtrl;
